import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const en = {
  nav: {
    home: 'Home', specialty: 'Specialty', education: 'Education', certificates: 'Certificates',
    experience: 'Experience', skills: 'Skills', portfolio: 'Portfolio', contact: 'Contact',
  },
  hero: {
    scrollDown: 'Scroll Down', viewWork: 'View My Work', downloadCV: 'Download CV',
    availableForWork: 'Available for work',
  },
  specialty: { title: 'Specialty', subtitle: 'What I bring to the table' },
  education: { title: 'Education', subtitle: 'Academic background', present: 'Present' },
  certificates: {
    title: 'Certificates',
    subtitle: 'Licenses & professional certifications',
    issued: 'Issued',
    expires: 'Expires',
    noExpiry: 'No Expiration',
    credentialId: 'Credential ID',
    verify: 'Verify Credential',
    present: 'Present',
  },
  experience: { title: 'Work Experience', subtitle: 'Professional journey & achievements', present: 'Present' },
  skills: { title: 'Skills', subtitle: 'Technologies & tools I work with' },
  portfolio: {
    title: 'Portfolio', subtitle: 'Selected projects & case studies',
    viewProject: 'View Project', viewCode: 'View Code', techStack: 'Tech Stack',
  },
  footer: {
    getInTouch: 'Get In Touch', sendMessage: 'Send Message',
    name: 'Name', email: 'Email', message: 'Message',
    copyright: 'All rights reserved.', language: 'Language',
    location: 'Location',
  },
  auth: {
    signIn: 'Sign In', signUp: 'Sign Up', logout: 'Logout',
    email: 'Email Address', password: 'Password', name: 'Full Name',
    enterOtp: 'Enter OTP', otpSent: 'OTP code sent to your email',
    resendOtp: 'Resend OTP', verify: 'Verify', back: 'Back',
    requestOtp: 'Request OTP', alreadyHaveAccount: 'Already have an account?',
    noAccount: "Don't have an account?",
    otpExpiry: 'Code expires in {{minutes}} minutes',
    registerSubtitle: 'Create your portfolio admin account',
    // GDPR consent checkbox
    gdprPrefix: 'I have read and agree to the',
    gdprTerms: 'Terms of Service',
    gdprAnd: 'and',
    gdprPrivacy: 'Privacy Policy',
    gdprSuffix: 'I consent to the processing of my personal data in accordance with GDPR.',
    continueWithGoogle: 'Continue with Google',
    signUpWithGoogle: 'Sign up with Google',
  },
  dashboard: {
    title: 'Dashboard', overview: 'Overview', sections: 'Sections',
    settings: 'Settings', theme: 'Theme', siteConfig: 'Site Config',
    save: 'Save Changes', cancel: 'Cancel', delete: 'Delete', edit: 'Edit',
    add: 'Add New', preview: 'Preview', upload: 'Upload', remove: 'Remove',
    order: 'Order', active: 'Active', inactive: 'Inactive',
    confirmDelete: 'Are you sure you want to delete this?',
    saved: 'Changes saved successfully!', error: 'An error occurred.',
    dragToReorder: 'Drag to reorder',
  },
  months: {
    january: 'January', february: 'February', march: 'March', april: 'April',
    may: 'May', june: 'June', july: 'July', august: 'August',
    september: 'September', october: 'October', november: 'November', december: 'December',
  },
  common: {
    loading: 'Loading...', error: 'Something went wrong', retry: 'Try again',
    close: 'Close', confirm: 'Confirm', noData: 'No data yet.', search: 'Search',
  },
  // ── Legal pages ──────────────────────────────────────────────────────────────
  legal: {
    terms: {
      title: 'Terms of Service',
      effectiveDate: 'Effective Date: January 1, 2026',
      intro: 'Please read these Terms of Service carefully before using PorttoCV. By creating an account and using our service, you agree to these terms. These terms govern your use of our platform and protect both your rights and ours.',
    },
    privacy: {
      title: 'Privacy Policy',
      effectiveDate: 'Effective Date: January 1, 2026',
      intro: 'At PorttoCV, your privacy is our priority. This Privacy Policy explains how we collect, use, store, and protect your personal data in compliance with the General Data Protection Regulation (GDPR) and the Turkish Personal Data Protection Law (KVKK). We believe in complete transparency about our data practices.',
    },
    contact: {
      title: 'Questions about this document?',
      email: 'Contact us at',
    },
  },
  landing: {
    nav: {
      features: 'Features',
      howItWorks: 'How It Works',
      portfolios: 'Portfolios',
      contact: 'Contact',
      signIn: 'Sign In',
      getStarted: 'Get Started Free',
    },
    hero: {
      badge: 'AI-Powered • ATS Optimized • Free Forever',
      headline1: 'Your Career Story,',
      headline2: 'Told Perfectly.',
      headline3: 'Every Time.',
      sub: 'Build a stunning portfolio website and generate ATS-optimized CVs with AI. Get noticed by top companies — for free.',
      ctaPrimary: 'Create Free Portfolio',
      ctaSecondary: 'Generate Free CV',
      trustLine: 'Trusted by professionals worldwide',
    },
    ats: {
      badge: 'Why PorttoCV?',
      headline: 'Your CVs Consistently Achieve',
      sub: 'Our AI-generated CVs are engineered to pass every major ATS system with flying colors.',
      score: 'ATS Parse Score',
      scoreDesc: 'Consistently achieved across Workday, Greenhouse, Lever, and Taleo — the four most widely used applicant tracking systems.',
      keyword: 'Keyword Density',
      keywordDesc: 'AI analyzes the job description and fills your CV with role-specific keywords that trigger ATS ranking algorithms.',
      callback: 'Interview Callback Rate',
      callbackDesc: 'Achievement-framed bullet points and quantified results dramatically increase recruiter response rates.',
      systems: 'Passes All Major ATS Systems',
    },
    howto: {
      badge: 'How-To CV Guides',
      headline: 'From Zero to Hired',
      sub: 'Follow our step-by-step guides to build a world-class portfolio and generate interview-winning CVs using AI.',
      steps: [
        {
          step: '01',
          title: 'Create Your Account',
          desc: 'Sign up for free in under 60 seconds. No credit card required. Your portfolio URL is reserved immediately.',
          tip: 'Pro tip: Choose a clean username like "firstname-lastname" for a professional portfolio URL.',
        },
        {
          step: '02',
          title: 'Build Your Portfolio',
          desc: 'Fill in your Hero section with a professional photo, your Education timeline, Work Experience, Skills with proficiency levels, and showcase your Projects.',
          tip: 'Pro tip: Add logo images for each company and university — it makes your timeline look stunning.',
        },
        {
          step: '03',
          title: 'Customize Your Theme',
          desc: 'Pick your brand colors, font, and layout from the dashboard. Your portfolio updates live — no coding needed.',
          tip: 'Pro tip: Dark themes with a strong accent color (indigo or cyan) perform best.',
        },
        {
          step: '04',
          title: 'Generate Your AI CV',
          desc: 'Go to Dashboard → AI CV Generator. Choose your template, set your accent color, toggle contact visibility, then click Generate.',
          tip: 'Pro tip: Use the Minimal template for the highest ATS score. Add the job description keywords to Custom Instructions.',
        },
        {
          step: '05',
          title: 'Download & Apply',
          desc: 'Download as .docx (best ATS compatibility) or .pdf. Your CV includes skill bars, achievement bullets, and a cover letter if requested.',
          tip: 'Pro tip: Always upload .docx to online job portals — never .pdf unless explicitly requested.',
        },
      ],
    },
    portfolios: {
      badge: 'Live Portfolios',
      headline: 'Built by Real Professionals',
      sub: 'Browse portfolios created with PorttoCV. Click any card to view the full portfolio.',
      empty: 'Be the first to create a portfolio!',
      viewPortfolio: 'View Portfolio →',
      experience: 'exp',
      projects: 'projects',
    },
    contact: {
      badge: 'Get In Touch',
      headline: "Let's Talk",
      sub: 'Have a question, feature request, or just want to say hi? We read every message.',
      name: 'Your Name',
      email: 'Your Email',
      subject: 'Subject',
      message: 'Your Message',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Message sent! We\'ll get back to you soon.',
      error: 'Failed to send.',
      namePlaceholder: 'John Doe',
      emailPlaceholder: 'john@example.com',
      subjectPlaceholder: 'I have a question about...',
      messagePlaceholder: 'Tell us what\'s on your mind...',
    },
    footer: {
      tagline: 'Build your career story. Land your dream job.',
      product: 'Product',
      features: 'Features',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      company: 'Company',
      about: 'About',
      blog: 'Blog',
      careers: 'Careers',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      language: 'Language',
      copyright: '© 2026 PorttoCV. All rights reserved.',
      madeWith: 'Made for job seekers worldwide',
    },
  },
};

const tr: typeof en = {
  nav: {
    home: 'Anasayfa', specialty: 'Uzmanlık', education: 'Eğitim', certificates: 'Sertifikalar',
    experience: 'Deneyim', skills: 'Beceriler', portfolio: 'Portföy', contact: 'İletişim',
  },
  hero: {
    scrollDown: 'Aşağı Kaydır', viewWork: 'Çalışmalarımı Gör', downloadCV: 'CV İndir',
    availableForWork: 'İş için müsaitim',
  },
  specialty: { title: 'Uzmanlık', subtitle: 'Masaya ne getiriyorum' },
  education: { title: 'Eğitim', subtitle: 'Akademik geçmiş ve sertifikalar', present: 'Günümüz' },
  certificates: {
    title: 'Sertifikalar',
    subtitle: 'Lisanslar ve profesyonel sertifikalar',
    issued: 'Verildi',
    expires: 'Geçerlilik',
    noExpiry: 'Süresiz',
    credentialId: 'Kimlik Numarası',
    verify: 'Sertifikayı Doğrula',
    present: 'Günümüz',
  },
  experience: { title: 'İş Deneyimi', subtitle: 'Profesyonel yolculuk ve başarılar', present: 'Günümüz' },
  skills: { title: 'Beceriler', subtitle: 'Kullandığım teknolojiler ve araçlar' },
  portfolio: {
    title: 'Portföy', subtitle: 'Seçilmiş projeler ve vaka çalışmaları',
    viewProject: 'Projeyi Gör', viewCode: 'Kodu Gör', techStack: 'Teknoloji Yığını',
  },
  footer: {
    getInTouch: 'İletişime Geç', sendMessage: 'Mesaj Gönder',
    name: 'İsim', email: 'E-posta', message: 'Mesaj',
    copyright: 'Tüm hakları saklıdır.', language: 'Dil',
    location: 'Konum',
  },
  auth: {
    signIn: 'Giriş Yap', signUp: 'Kayıt Ol', logout: 'Çıkış Yap',
    email: 'E-posta Adresi', password: 'Şifre', name: 'Ad Soyad',
    enterOtp: 'OTP Girin', otpSent: 'E-postanıza OTP kodu gönderildi',
    resendOtp: 'OTP Tekrar Gönder', verify: 'Doğrula', back: 'Geri',
    requestOtp: 'OTP İste', alreadyHaveAccount: 'Zaten hesabınız var mı?',
    noAccount: 'Hesabınız yok mu?',
    otpExpiry: 'Kod {{minutes}} dakika içinde geçersiz olur',
    registerSubtitle: 'Portföy yönetici hesabınızı oluşturun',
    // GDPR consent checkbox
    gdprPrefix: 'Okudum ve kabul ediyorum:',
    gdprTerms: 'Kullanım Koşulları',
    gdprAnd: 've',
    gdprPrivacy: 'Gizlilik Politikası',
    gdprSuffix: 'Kişisel verilerimin GDPR/KVKK kapsamında işlenmesine onay veriyorum.',
    continueWithGoogle: 'Google ile Devam Et',
    signUpWithGoogle: 'Google ile Kayıt Ol',
  },
  dashboard: {
    title: 'Panel', overview: 'Genel Bakış', sections: 'Bölümler',
    settings: 'Ayarlar', theme: 'Tema', siteConfig: 'Site Ayarları',
    save: 'Değişiklikleri Kaydet', cancel: 'İptal', delete: 'Sil', edit: 'Düzenle',
    add: 'Yeni Ekle', preview: 'Önizle', upload: 'Yükle', remove: 'Kaldır',
    order: 'Sıra', active: 'Aktif', inactive: 'Pasif',
    confirmDelete: 'Bunu silmek istediğinizden emin misiniz?',
    saved: 'Değişiklikler başarıyla kaydedildi!', error: 'Bir hata oluştu.',
    dragToReorder: 'Sıralamak için sürükleyin',
  },
  months: {
    january: 'Ocak', february: 'Şubat', march: 'Mart', april: 'Nisan',
    may: 'Mayıs', june: 'Haziran', july: 'Temmuz', august: 'Ağustos',
    september: 'Eylül', october: 'Ekim', november: 'Kasım', december: 'Aralık',
  },
  common: {
    loading: 'Yükleniyor...', error: 'Bir şeyler yanlış gitti', retry: 'Tekrar dene',
    close: 'Kapat', confirm: 'Onayla', noData: 'Henüz veri yok.', search: 'Ara',
  },
  // ── Yasal sayfalar ────────────────────────────────────────────────────────────
  legal: {
    terms: {
      title: 'Kullanım Koşulları',
      effectiveDate: 'Yürürlük Tarihi: 1 Ocak 2026',
      intro: 'PorttoCV\'yi kullanmadan önce bu Kullanım Koşullarını dikkatlice okuyunuz. Hesap oluşturarak ve hizmetimizi kullanarak bu koşulları kabul etmiş olursunuz. Bu koşullar, platformumuzu kullanımınızı düzenler ve hem sizin hem de bizim haklarımızı korur.',
    },
    privacy: {
      title: 'Gizlilik Politikası',
      effectiveDate: 'Yürürlük Tarihi: 1 Ocak 2026',
      intro: 'PorttoCV\'de gizliliğiniz önceliğimizdir. Bu Gizlilik Politikası, kişisel verilerinizi Genel Veri Koruma Yönetmeliği (GDPR) ve 6698 Sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında nasıl topladığımızı, kullandığımızı, sakladığımızı ve koruduğumuzu açıklar. Veri uygulamalarımız konusunda tam şeffaflığa inanıyoruz.',
    },
    contact: {
      title: 'Bu belge hakkında sorularınız mı var?',
      email: 'Bize ulaşın',
    },
  },
  landing: {
    nav: {
      features: 'Özellikler',
      howItWorks: 'Nasıl Çalışır',
      portfolios: 'Portföyler',
      contact: 'İletişim',
      signIn: 'Giriş Yap',
      getStarted: 'Ücretsiz Başla',
    },
    hero: {
      badge: 'Yapay Zeka • ATS Optimize • Ücretsiz',
      headline1: 'Kariyer Hikayeniz,',
      headline2: 'Mükemmel Anlatılsın.',
      headline3: 'Her Seferinde.',
      sub: 'Yapay zeka ile ATS uyumlu CV\'ler oluşturun ve etkileyici portföy web siteleri kurun. Ücretsiz.',
      ctaPrimary: 'Ücretsiz Portföy Oluştur',
      ctaSecondary: 'Ücretsiz CV Oluştur',
      trustLine: 'Dünya genelinde profesyoneller tarafından kullanılıyor',
    },
    ats: {
      badge: 'Neden PorttoCV?',
      headline: 'CV\'leriniz Sürekli Olarak Elde Eder',
      sub: 'Yapay zeka destekli CV\'lerimiz tüm büyük ATS sistemlerinden geçecek şekilde tasarlanmıştır.',
      score: 'ATS Ayrıştırma Skoru',
      scoreDesc: 'Workday, Greenhouse, Lever ve Taleo\'da sürekli olarak elde edilir.',
      keyword: 'Anahtar Kelime Yoğunluğu',
      keywordDesc: 'Yapay zeka iş ilanını analiz eder ve CV\'nizi tetikleyici anahtar kelimelerle doldurur.',
      callback: 'Mülakat Geri Dönüş Oranı',
      callbackDesc: 'Başarı odaklı maddeler ve somut sonuçlar işe alım yanıt oranlarını önemli ölçüde artırır.',
      systems: 'Tüm Büyük ATS Sistemlerini Geçer',
    },
    howto: {
      badge: 'CV Rehberleri',
      headline: 'Sıfırdan İşe Alıma',
      sub: 'Adım adım rehberlerimizle dünya standartlarında bir portföy oluşturun ve yapay zeka ile CV üretin.',
      steps: [
        { step: '01', title: 'Hesap Oluştur', desc: '60 saniyede ücretsiz kayıt olun. Kredi kartı gerekmez.', tip: 'İpucu: "ad-soyad" formatında kullanıcı adı seçin.' },
        { step: '02', title: 'Portföyünü Oluştur', desc: 'Fotoğraf, eğitim, deneyim, beceriler ve projelerinizi ekleyin.', tip: 'İpucu: Şirket ve üniversite logoları ekleyin.' },
        { step: '03', title: 'Temanı Özelleştir', desc: 'Renkler, yazı tipi ve düzeni panelden ayarlayın.', tip: 'İpucu: Koyu temalar en iyi performansı gösterir.' },
        { step: '04', title: 'AI CV Oluştur', desc: 'Panel → AI CV Oluşturucu. Şablon seçin, renk ayarlayın, oluştur\'a tıklayın.', tip: 'İpucu: En yüksek ATS skoru için Minimal şablonu kullanın.' },
        { step: '05', title: 'İndir ve Başvur', desc: '.docx veya .pdf olarak indirin. Beceri çubukları ve cover letter dahildir.', tip: 'İpucu: Online portallara her zaman .docx yükleyin.' },
      ],
    },
    portfolios: {
      badge: 'Canlı Portföyler',
      headline: 'Gerçek Profesyoneller Tarafından Oluşturuldu',
      sub: 'PorttoCV ile oluşturulmuş portföylere göz atın.',
      empty: 'İlk portföyü oluşturan sen ol!',
      viewPortfolio: 'Portföyü Gör →',
      experience: 'deneyim',
      projects: 'proje',
    },
    contact: {
      badge: 'İletişime Geç',
      headline: 'Konuşalım',
      sub: 'Soru, öneri veya merhaba demek için yazın. Her mesajı okuyoruz.',
      name: 'Adınız',
      email: 'E-postanız',
      subject: 'Konu',
      message: 'Mesajınız',
      send: 'Mesaj Gönder',
      sending: 'Gönderiliyor...',
      success: 'Mesaj gönderildi! En kısa sürede dönüş yapacağız.',
      error: 'Gönderilemedi.',
      namePlaceholder: 'Ali Yılmaz',
      emailPlaceholder: 'ali@ornek.com',
      subjectPlaceholder: 'Hakkında sorum var...',
      messagePlaceholder: 'Ne düşündüğünüzü yazın...',
    },
    footer: {
      tagline: 'Kariyer hikayeni anlat. Hayalindeki işe kavuş.',
      product: 'Ürün',
      features: 'Özellikler',
      howItWorks: 'Nasıl Çalışır',
      pricing: 'Fiyatlandırma',
      company: 'Şirket',
      about: 'Hakkında',
      blog: 'Blog',
      careers: 'Kariyer',
      legal: 'Yasal',
      privacy: 'Gizlilik Politikası',
      terms: 'Kullanım Koşulları',
      language: 'Dil',
      copyright: '© 2026 PorttoCV. Tüm hakları saklıdır.',
      madeWith: 'Dünya genelindeki iş arayanlar için ❤️ ile yapıldı',
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, tr: { translation: tr } },
    fallbackLng: 'en',
    supportedLngs: ['en', 'tr'],
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  });

export default i18n;